import Vue from 'vue';
import Vuex from 'vuex';
import * as API from '@/api';

import { artwork } from './artwork';
import { temple } from './temple';
import { theme } from './theme';
import { album } from './album';
import { tag } from './tag';
import { search } from './search';
import { feature } from './feature';
import { member } from './member';
import { favourite } from './favourite';
import { festivalYear } from './festivalYear';
import { filter } from './filter'

const festivalCategories = [
  'activityInfo',
  'newInfo',
  'english',
  'skill',
  'festival',
];

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    productionHostname: `https://www.tainan-temple-tour.net`,
    windowWidth: 0,
    homepageData: {
      articles: [],
      themes: [],
      temples: [],
      albums: [],
      randomTags: [],
    },
    homepageFestivalYear: {
      festivals: [],
      infos: [],
      skills: [],
      times: [],
    },
    nearestTemple: {},
    geoCodedPosition: {
      lat: 22,
      lng: 120,
    },
    params: {},
  },
  getters: {
    windowWidth(state) {
      return state.windowWidth;
    },
    productionHostname(state) {
      return state.productionHostname;
    },
    geoCodedPosition(state) {
      return state.geoCodedPosition;
    },
    getCoverMedia: (state) => (module) => {
      const realMainModule = festivalCategories.includes(module)
        ? state.festivalYear[module]
        : state[module][module];
      const video = realMainModule.cover_video;
      const image = realMainModule.cover_image_url;
      let videoParam;

      if (video) {
        // 當網址是 https://www.youtube.com/watch?v=qgICMuxC4RY 或是 https://youtu.be/qgICMuxC4RY
        videoParam = video.search('watch')
          ? video.split('/')[3].split('?v=')[1]
          : video.split('/')[3];
      }

      return video
        ? {
            type: 'video',
            reference: videoParam,
            description: realMainModule.cover_video_description,
          }
        : {
            type: 'image',
            reference: image,
            description: realMainModule.cover_image_description,
          };
    },
    getSubParagraphMedia: (state) => (module, pIndex, isNested, paragraph) => {
      const realMainModule = festivalCategories.includes(module)
        ? state.festivalYear[module]
        : state[module][module];

      // 當不是巢狀載入時（除了廟宇之外）
      // 沒主段落直接中斷執行
      if (isNested === false && module !== 'theme') {
        if (!realMainModule.paragraphs || !realMainModule.paragraphs.length) {
          return;
        }
      }

      if (!isNested) {
        if (paragraph.type === 1 && paragraph.reference) {
          // 只有廟宇是巢狀
          // 因為旅遊的段落結構不一樣
          // 藝品：paragraph
          // 旅遊：paragraph.reference
          paragraph = paragraph.reference;
        }
      }

      return paragraph.sub_paragraphs.map((sub, sIndex, allSubs) => {
        if (sub.reference) {
          // 若次段落有值
          // 只針對影片類型拿出參數
          let videoParam;

          if (sub.type === 2) {
            videoParam = sub.reference.search('watch')
              ? sub.reference.split('/')[3].split('?v=')[1]
              : sub.reference.split('/')[3];
          }

          // 回傳結果
          return sub.type === 1
            ? {
                type: 'image',
                reference: sub.cover_image_url,
                description: sub.description,
              }
            : {
                type: 'video',
                reference: videoParam,
                description: sub.description,
              };
        } else {
          allSubs.splice(sIndex, 1);
        }
      });
    },
    // 取得首頁旅遊、廟宇、圖鑑集
    homepageArticles: (state) => (articleType) => {
      const articles = state.homepageData[articleType];
      const homepageData = articles
        .filter((article, articleIndex) => {
          return articleIndex < 5;
        })
        .map((article) => {
          // 統一使用 id 作為唯一識別欄位
          article.id = article.reference_id;

          if (articleType === 'temples') {
            article.name = article.title;
          }

          return article;
        });

      return homepageData;
    },
    // 首頁置頂文章
    homepageTopArticles(state) {
      let topArticles = state.homepageData.articles;

      topArticles.map((topArticle) => {
        switch (topArticle.reference_type) {
          case 1:
            topArticle.linkPath = 'album';
            break;
          case 2:
            topArticle.linkPath = 'theme';
            break;
          case 3:
            topArticle.linkPath = 'temple';
            break;
          case 4:
            topArticle.linkPath = 'artwork';
            break;
          default:
            topArticle.linkPath = '';
            break;
        }
      });

      return topArticles;
    },
    // 最近廟宇
    nearestTemple(state) {
      return state.nearestTemple;
    },
    // 取得亂數標籤
    randomTags(state) {
      state.homepageData.randomTags.push({
        name: '更多標籤',
        pathName: 'tags',
      });
      return state.homepageData.randomTags;
    },
    homeFestivals(state) {
      return state.homepageFestivalYear.festivals;
    },
    homeInfos(state) {
      return state.homepageFestivalYear.infos;
    },
    homeSkills(state) {
      return state.homepageFestivalYear.skills;
    },
    homeTimes(state) {
      return state.homepageFestivalYear.times;
    },
  },
  mutations: {
    setGeoCodedPosition(state, payload) {
      state.geoCodedPosition = payload;
    },
    setWindowWidth(state, payload) {
      state.windowWidth = payload;
    },
    setUpdatedArticles(state, { category, articles }) {
      // 取得文章列表的 store module 名稱
      const festivalYearAPIs = [
        'festivals',
        'skills',
        'activityInfos',
        'newInfos',
        'englishes',
      ];

      const articleModuleName = festivalYearAPIs.includes(category)
        ? 'festivalYear'
        : category.slice(0, category.length - 1);

      // 更新文章列表
      state[articleModuleName][category] = articles;
    },
    setHomepageArticles(
      state,
      { articles = [], themes = [], temples = [], albums = [] }
    ) {
      state.homepageData.articles = articles;
      state.homepageData.themes = themes;
      state.homepageData.temples = temples;
      state.homepageData.albums = albums;
    },
    setNearestTemple(state, nearestTemple) {
      state.nearestTemple = nearestTemple;
    },
    setRandomTags(state, randomTags) {
      state.homepageData.randomTags = randomTags;
    },
    setHomeFestivals(state, festivals) {
      state.homepageFestivalYear.festivals = festivals;
    },
    setHomeInfos(state, infos) {
      state.homepageFestivalYear.infos = infos;
    },
    setHomeSkills(state, skills) {
      state.homepageFestivalYear.skills = skills;
    },
    setHomeTimes(state, times) {
      state.homepageFestivalYear.times = times;
    },
    setArticleParams(state, params) {
      state.params = params;
    }
  },
  actions: {
    // 當文稿沒有經緯度，就呼叫此函式
    // 給 Google maps API 地址資訊，取得反向經緯度
    async getGeoCodedPosition({ commit }, { address }) {
      const GOOGLE_MAP_KEY = 'AIzaSyA8hVl2rmM70yal9Uh_6yW-K0KjeJpZWeI';

      const geoCodedJSONPosition = await API.GET(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${GOOGLE_MAP_KEY}`
      ).then((data) => {
        return data.data.results[0].geometry.location;
      });

      commit('setGeoCodedPosition', geoCodedJSONPosition);
    },

    // 依據頁數更新文章
    async getUpdatedArticles({ commit, state }, { category, page }) {
      const articles = await API.GET(`/${category}`, {
        limit: 6,
        page,
        ...state.params
      })

      commit('setUpdatedArticles', {
        category: category,
        articles: articles.data,
      });
    },
    // 首頁的置頂文章、旅遊、廟宇、圖鑑集
    async getHomeRelations({ commit }) {
      const homepageArticles = await API.GET(`/home/relations`);

      commit('setHomepageArticles', homepageArticles.data);
    },
    // 取得最近廟宇
    async getNearestTemple({ commit }, { lat, lng }) {
      const nearestTemple = await API.GET(
        `/nearest-temple?lat=${lat}&lng=${lng}`
      );

      commit('setNearestTemple', nearestTemple.data);
    },
    // 獲得亂數標籤
    async getHomeTags({ commit }, tagAmount) {
      const randomTags = await API.GET(`/tags?limit=${tagAmount}`);

      commit('setRandomTags', randomTags.data.tags);
    },
    async getHomeFestivalYear({ commit }) {
      const { data = {} } = await API.GET(`/home`);
      const { festivals, infos, skills, times } = data;

      commit('setHomeFestivals', festivals);
      commit('setHomeInfos', infos);
      commit('setHomeSkills', skills);
      commit('setHomeTimes', times);
    },
  },
  modules: {
    artwork,
    temple,
    theme,
    album,
    tag,
    search,
    feature,
    member,
    favourite,
    festivalYear,
    filter
  },
});
